import { ClientPortfolioReport } from "@/modules/reports/pages/client-portfolio";
import { createFileRoute } from "@tanstack/react-router";
import {
  clientDetailsQueryOptions,
  distributorEmployeeByIdQueryOptions,
  generateReportData,
  getDistributorBrandingOptions,
  getDistributorProfileOptions,
  getUserOptions,
  portfolioCategorySummaryOptions,
} from "creso-core/hooks";
import {
  buildClientProductBreakdownItems,
  findBreakdownByCategory,
  getAssetAllocationBucketsFromBreakdown,
} from "creso-core/utils";
import { PortfolioBreakdown } from "creso-core/types";
import { EPOCH_DATE } from "creso-core/constants";
import { ASSET_ALLOCATION_BUCKET_COLORS } from "@crm/components/overview/asset-allocation";
import { portfolioSourceSchema } from "creso-core/zod";
import { format } from "date-fns";
import { boolean, object, string } from "zod";

export const Route = createFileRoute(
  "/(auth)/(headless)/reports/clients/$id/portfolio"
)({
  component: ClientPortfolioReport,
  validateSearch: object({
    source: portfolioSourceSchema.optional(),
    date_range: string().optional(),
    start_date: string().optional(),
    end_date: string().optional(),
    include_transactions: boolean().optional(),
  }),
  loaderDeps: ({ search }) => ({
    source: search.source,
    date_range: search.date_range,
    start_date: search.start_date,
    end_date: search.end_date,
    include_transactions: search.include_transactions,
  }),
  loader: async ({ context, params, deps }) => {
    const { queryClient } = context;
    const userPromise = queryClient.fetchQuery(getUserOptions(true));
    const clientPromise = queryClient.fetchQuery(
      clientDetailsQueryOptions(Number(params.id))
    );
    const distributorProfilePromise = queryClient.fetchQuery(
      getDistributorProfileOptions(true)
    );
    const distributorBrandingPromise = queryClient.fetchQuery(
      getDistributorBrandingOptions(true)
    );

    const {
      source = "OVERALL",
      date_range,
      start_date,
      end_date,
      include_transactions,
    } = deps;

    const resolvedDateRange = date_range ?? "till_today";
    const reportType =
      resolvedDateRange === "till_today" ? "holdings" : "statement";
    const user = await userPromise;
    const metadata =
      user?.metadata && typeof user.metadata === "object"
        ? (user.metadata as Record<string, unknown>)
        : null;
    const rawEmployeeId = metadata?.id ?? null;
    const employeeId =
      user?.user_type === "employee"
        ? typeof rawEmployeeId === "number"
          ? rawEmployeeId
          : typeof rawEmployeeId === "string"
            ? Number(rawEmployeeId)
            : undefined
        : undefined;

    const employeePromise =
      employeeId != null && user?.user_type === "employee"
        ? queryClient.fetchQuery(
            distributorEmployeeByIdQueryOptions(employeeId, { enabled: true })
          )
        : Promise.resolve(null);

    const client = await clientPromise;

    if (!client) {
      throw new Error("Client not found");
    }

    if (!client.investment_account) {
      throw new Error("Client has no investment account");
    }

    const portfolioJson = await generateReportData<
      "holdings" | "statement",
      "json"
    >({
      file_type: "json",
      type: reportType,
      investment_account_guid: client.investment_account,
      source,
      start_date: start_date ?? EPOCH_DATE,
      end_date: end_date ?? format(new Date(), "yyyy-MM-dd"),
      additional_details: {
        include_transactions: false,
      },
    }).catch(() => null);

    const holdings = portfolioJson?.holdings ?? [];

    const sips = portfolioJson?.ongoing_xsip ?? [];

    const ordersJson = include_transactions
      ? await generateReportData<"recent_transactions", "json">({
          file_type: "json",
          type: "recent_transactions",
          investment_account_guid: client.investment_account,
          start_date:
            date_range === "till_today" ? EPOCH_DATE : (start_date ?? ""),
          end_date: end_date ?? format(new Date(), "yyyy-MM-dd"),
          additional_details: {
            order_status: "COMPLETED",
          },
        }).catch(() => null)
      : null;

    const transactions = ordersJson?.transactions ?? [];

    const portfolioBreakdown = await queryClient
      .fetchQuery(
        portfolioCategorySummaryOptions({
          investmentAccountId: client.investment_account,
        })
      )
      .catch(() => [] as PortfolioBreakdown[]);

    const breakdownBySource = findBreakdownByCategory(
      portfolioBreakdown,
      source
    );

    const allocationBuckets = getAssetAllocationBucketsFromBreakdown(
      breakdownBySource,
      ASSET_ALLOCATION_BUCKET_COLORS
    );

    const distributorProfile = await distributorProfilePromise;

    const productBreakdownItems = buildClientProductBreakdownItems(
      breakdownBySource,
      distributorProfile
    );

    return {
      client,
      distributorProfile,
      distributorBranding: await distributorBrandingPromise,
      employee: await employeePromise,
      summary: portfolioJson?.summary ?? undefined,
      holdings,
      sips,
      transactions,
      allocationBuckets,
      productBreakdownItems,
    };
  },
});
